import React from 'react';
import DynamicCard from './DynamicCard';
import { getArticulationStats } from '../data/mockProject';

const ArticulationCard = ({
  articulation,
  number,
  scoreType,
  slug,
  accessToken,
  onUploadComplete,
  portalUploadBlockedReason = null,
  previewLimitReason = null,
  previewOnFirstDynamic = false,
}) => {
  const stats = getArticulationStats(articulation);
  const hasNeedsAttention = stats.needsAttention > 0;
  const allApproved = stats.approved === stats.total;

  return (
    <div className={`articulation-card ${allApproved ? 'is-complete' : ''} ${hasNeedsAttention ? 'needs-attention' : ''}`}>
      {/* Articulation title */}
      <h3 className="articulation-title">
        {scoreType} Articulation No.{number}: "{articulation.name}"
      </h3>

      {/* Instructions - if present */}
      {articulation.instructions && (
        <p className="articulation-body-text">{articulation.instructions}</p>
      )}

      {/* Guide audio - if this articulation has a play-along/guide track */}
      {articulation.guide_url && (
        <div className="guide-audio">
          <div className="guide-audio-header">
            <svg className="guide-audio-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
            <div className="guide-audio-text">
              <span className="guide-audio-title">Play-along guide track</span>
              <span className="guide-audio-hint">
                This is a call-and-response recording — the guide plays each note, then you repeat it back.
              </span>
            </div>
            <a
              className="guide-audio-download"
              href={articulation.guide_url}
              download
              target="_blank"
              rel="noopener noreferrer"
            >
              Download
            </a>
          </div>

          <ol className="guide-audio-steps">
            <li>
              <span className="guide-audio-step-num">1</span>
              <span>Put this track in your <strong>headphones</strong> — it must never be heard in your recording.</span>
            </li>
            <li>
              <span className="guide-audio-step-num">2</span>
              <span>It plays a note (with a click), then goes quiet. <strong>Clicks = listen. Silence = your turn.</strong></span>
            </li>
            <li>
              <span className="guide-audio-step-num">3</span>
              <span>Sing or play the note back in the quiet gap. Hold it for its full length, then wait for the next one.</span>
            </li>
            <li>
              <span className="guide-audio-step-num">4</span>
              <span>Press record and go through the whole score in <strong>one continuous take</strong> — two clicks count you in at the start.</span>
            </li>
          </ol>

          <audio
            className="guide-audio-player"
            controls
            preload="none"
            src={articulation.guide_url}
          >
            Your browser does not support audio playback.
            <a href={articulation.guide_url}>Download the guide audio</a> instead.
          </audio>
        </div>
      )}

      {/* For articulation - intro text */}
      <p className="articulation-body-text">
        For articulation "{articulation.name}", complete the following recording{articulation.dynamics.length > 1 ? 's' : ''}:
      </p>

      {/* Dynamics wrinkle - one score/guide, recorded once per dynamic */}
      {articulation.guide_url && articulation.dynamics.length > 1 && (
        <p className="articulation-note">
          These all use the same score and guide track — record it once for each dynamic below, singing/playing at the volume marked.
        </p>
      )}

      {/* Dynamics list - each dynamic is a recording to submit */}
      <div className="dynamics-list">
        {articulation.dynamics.map((dynamic, index) => (
          <DynamicCard 
            key={dynamic.id} 
            dynamic={dynamic}
            articulationName={articulation.name}
            scoreUrl={articulation.score_url}
            slug={slug}
            accessToken={accessToken}
            onUploadComplete={onUploadComplete}
            portalUploadBlockedReason={portalUploadBlockedReason}
            previewLimitReason={
              previewOnFirstDynamic && index === 0 ? previewLimitReason : null
            }
          />
        ))}
      </div>
    </div>
  );
};

export default ArticulationCard;
